import { Button, Clickable, Col, Hidden, Row, Span } from '@ahmdigital/ui';
import { endsWith } from 'lodash/fp';
import { EXTRAS_TYPE } from '@ahmdigital/constants';
import { useRouter } from 'next/router';

import { HeaderWrapper, LargeDevicePremiumReviewToggleWrapper, StyledStrong } from './styles';
import { MEDIA_SIZE, PREMIUM_REVIEW_TOGGLE_STYLE } from '../../ahm-constants/ui';
import { MediaDisplay } from '../../styles/styles';
import { ProductType } from '../../types/product';
import constants from '../../ahm-constants';
import DesktopHeaderFilterBar from './components/filter-bar-header';
import getRoutePath from '../../utils/get-route-path';
import getTitleFromType from '../../utils/get-title-from-type';
import MoreOptionsLink from './components/more-options-link';
import NavigationHeaderTitle, { NavigationUpliftHeaderTitle } from './components/navigation-header-title';
import PremiumReviewToggle from '../premium-review-toggle-wrapper';
import styles from './styles.module.sass';
import UpliftedHeader from './components/filter-bar-header/uplifted-header';

type NavigationHeaderProps = {
  isCombiningHospitalWithExtras?: boolean;
  isProductListingPage?: boolean;
  isProductListingPageAll?: boolean;
  needsProductType?: ProductType['extrasType'];
  needsType?: string;
  productExtrasType?: ProductType['extrasType'];
  productQuantity?: number;
  productShortName?: string;
  productType: string;
  products?: ProductType[];
  showPremiumReviewToggle?: boolean;
  totalProducts?: number;
  useHeading?: boolean;
};

const NavigationHeader = ({
  isCombiningHospitalWithExtras = false,
  needsProductType,
  needsType,
  productExtrasType,
  productQuantity,
  totalProducts,
  products,
  productShortName,
  productType,
  showPremiumReviewToggle = true,
  useHeading = false,
  isProductListingPage,
  isProductListingPageAll,
}: NavigationHeaderProps) => {
  const router = useRouter();
  const routePath = getRoutePath(router.asPath);

  const isChoosableExtra = productExtrasType === EXTRAS_TYPE.CHOOSABLE;
  let changeUrl = constants.URLS.SALES.REFINE_PRICE;

  const isProductListingPageAdd = endsWith('/add', routePath);
  const isProductPage = !(isProductListingPage || isProductListingPageAdd);

  if (isChoosableExtra) {
    changeUrl = constants.URLS.SALES.CHOOSABLE_PICKER;
  }

  const title = isChoosableExtra ? productShortName : getTitleFromType({ needsProductType, needsType, productType });

  const upliftTitle = `Showing ${totalProducts} ${title.substring(0, title.lastIndexOf(' '))} covers.`;

  const productQuantityText =
    totalProducts && productQuantity !== undefined && productQuantity < totalProducts
      ? `${productQuantity} of ${totalProducts}`
      : productQuantity;

  let titlePrefix = productQuantity ? `${productQuantityText} options for` : 'I’m looking for';
  if (isCombiningHospitalWithExtras) {
    titlePrefix = 'Add';
  }

  const titleSuffix = totalProducts && productQuantity && productQuantity < totalProducts ? 'based on the below' : '';

  const renderPremiumReviewToggle = (isProductListing: boolean) => {
    const ToggleComponent = (
      <PremiumReviewToggle
        type={PREMIUM_REVIEW_TOGGLE_STYLE.FLAT_WHITE}
        isProductPage={!isProductListing}
        showBackground={false}
      />
    );
    if (isProductListing)
      return (
        <MediaDisplay up media={MEDIA_SIZE.MD}>
          <LargeDevicePremiumReviewToggleWrapper>{ToggleComponent}</LargeDevicePremiumReviewToggleWrapper>
        </MediaDisplay>
      );

    return <MediaDisplay media={MEDIA_SIZE.MD}>{ToggleComponent}</MediaDisplay>;
  };

  const renderPremiumReviewToggleUplift = () => {
    const ToggleComponent = (
      <>
        <Hidden xs sm>
          <PremiumReviewToggle showBackground={false} type={PREMIUM_REVIEW_TOGGLE_STYLE.INLINE} />
        </Hidden>
        <Hidden md df lg>
          <PremiumReviewToggle showBackground={false} type={PREMIUM_REVIEW_TOGGLE_STYLE.COMPARER_PAGE} />
        </Hidden>
      </>
    );

    return ToggleComponent;
  };

  const filterBarProps = {
    changeUrl,
    isCombiningHospitalWithExtras,
    isProductListingPage,
    isProductListingPageAdd,
    isProductListingPageAll,
    needsProductType,
    needsType,
    products,
    showPremiumReviewToggle,
    title,
    titlePrefix,
    titleSuffix,
    useHeading,
  };

  return (
    <>
      <HeaderWrapper>
        <MediaDisplay media={MEDIA_SIZE.MD} up>
          <DesktopHeaderFilterBar {...filterBarProps} />
        </MediaDisplay>
        <MediaDisplay media={MEDIA_SIZE.MD}>
          {isProductListingPageAll ? (
            <UpliftedHeader needsType={needsType ?? undefined} products={products ?? undefined} />
          ) : (
            <div className={styles.wrapper}>
              <div className={styles.gridWrapper}>
                {isCombiningHospitalWithExtras && (
                  <Clickable
                    isUnstyled
                    icon={{ name: 'chevron-left' }}
                    onClick={router.back}
                    variant={styles.backButton}
                  >
                    <Hidden xs sm>
                      Back
                    </Hidden>
                  </Clickable>
                )}
                <div className={styles.gridColumns}>
                  <Row lgHorizontalAlign="center" verticalAlign="middle">
                    <Col sm={12}>
                      <NavigationHeaderTitle useHeading={useHeading}>
                        <Span data-testid="md-navigation-header-title">
                          {titlePrefix} <StyledStrong>{title}</StyledStrong> {titleSuffix}
                        </Span>
                      </NavigationHeaderTitle>
                    </Col>
                    {!isCombiningHospitalWithExtras && (
                      <Col sm={12}>
                        <Button to={changeUrl} className={styles.button} variant="white">
                          Change this
                        </Button>
                        <MoreOptionsLink className={styles.moreOptionsLink} />
                      </Col>
                    )}
                  </Row>
                </div>
              </div>
            </div>
          )}
        </MediaDisplay>
        {!isProductListingPageAll && showPremiumReviewToggle && renderPremiumReviewToggle(!isProductPage)}
      </HeaderWrapper>
      {isProductListingPageAll && (
        <NavigationUpliftHeaderTitle useHeading={useHeading}>
          <Span data-testid="md-navigation-header-title">{upliftTitle}</Span> <MoreOptionsLink />
          {showPremiumReviewToggle && renderPremiumReviewToggleUplift()}
        </NavigationUpliftHeaderTitle>
      )}
    </>
  );
};

export { NavigationHeader as NavigationHeaderForTest };

export default NavigationHeader;
